/** @jsx jsx */

import React, { useEffect, useMemo } from "react";

import { jsx } from "@emotion/core";

import { Formik, Form } from "formik";
import {
  Typography,
  Divider,
  Button,
  message,
  Row,
  Col,
  Icon,
  Tooltip
} from "antd";

import InfoIcon from "../../assets/info-tooltip.svg";

import FormDropdown from "../../components/formHelpers/FormDropdown";
import FormFileUpload, {
  FileStatus
} from "../../components/formHelpers/FormFileUpload";
import SubmitButton from "../../components/button";

import { downloadBlobFile } from "../../helper/DownloadFile";
import useAPI from "../../helperHooks/useAPI";
import useEligibilityMetaData from "../../hooks/auth/useEligibilityMetaData";
import useLoanStatus from "../../hooks/auth/useLoanStatus";

import { ServiceType } from "../../services/ServiceType.data";
import { DocumentsService } from "../../services/KYCDocumentsService";
import {
  DocumentDetails,
  KYCDocumentsList,
  KYCFileCollection
} from "../../services/KYCDocumentsService/KYCDocumentsService.type";

import {
  KYCContents as CONSTANTS,
  KYCValidationSchema as validationSchema,
  KYC_SUPPORTED_EXTENSIONS
} from "./KYCConstants";
import { KYCStyles } from "./KYCStyles";
import { useHistory } from "react-router-dom";
import { routes } from "Routes";

const { Text } = Typography;

const KYC: React.FC = () => {
  const { callAPI: generatePDF } = useAPI(DocumentsService.generatePDF);
  const { callAPI: getKYCDocumentsList, ...getDocumentsList } = useAPI(
    DocumentsService.getKYCDocumentsList
  );
  const {
    callAPI: getKYCUploadedDocumentList,
    ...getUploadedDocuments
  } = useAPI(DocumentsService.getKYCUploadedDocuments);
  const { callAPI: submitKYC, ...KYCSubmit } = useAPI(
    DocumentsService.submitKYC
  );

  const {
    eligibilityMetaData: { eligibilityId, isGstinInactive, gstin }
  } = useEligibilityMetaData();
  const { disableKYC, updateStatus, dmiInitiated } = useLoanStatus();

  useEffect(() => {
    getKYCDocumentsList({});
    if (eligibilityId) {
      getKYCUploadedDocumentList(eligibilityId);
    }
  }, [getKYCDocumentsList, getKYCUploadedDocumentList, eligibilityId]);

  const history = useHistory();

  const documents: {
    options: KYCDocumentsList;
    data: KYCFileCollection;
  } = useMemo(() => {
    const mandatoryDocuments = {
      POI: [],
      POB: [],
      POA: [],
      "Loan Application Form": [],
      "Loan Detail Sheet": [],
      "Post-dated Cheque": [],
      "Nach File Scan": [],
    };
    const options = getDocumentsList.response || mandatoryDocuments;
    const uploadedDocList = {
      POIDocType: "",
      POADocType: "",
      POBDocType: "",
      ...mandatoryDocuments
    };
    Object.keys(options).forEach(key => {
      uploadedDocList[key] = [];
    });
    (getUploadedDocuments.response || []).forEach((doc: DocumentDetails) => {
      (doc?.docType && uploadedDocList[doc?.docType]) && uploadedDocList[doc?.docType].push({
        id: doc.id,
        name: doc.fileName,
        status: FileStatus.Success,
        removed: false,
        subDocType: doc.subDocType
      });
    });
    return { options, data: uploadedDocList };
  }, [getDocumentsList.response, getUploadedDocuments.response]);

  const fileConfig = (docType: string, subDocType?: string) => {
    return {
      uploadServiceType: ServiceType.Eligibility,
      uploadPath: DocumentsService.uploadKYCDocumentPath,
      uploadParams: {
        subDocType: subDocType ? subDocType : docType,
        docType
      },
      deletePath: DocumentsService.deleteKYCDocumentPath
    };
  };

  let fieldCounter = 0;

  const handleSubmit = () => {
    if (isGstinInactive) {
      message.error(CONSTANTS.feedbacks.inactiveGSTIN(gstin));
    } else {
      submitKYC({}, response => {
        message.success(CONSTANTS.feedbacks.DMISubmitted);
        //refresh the eligibility status
        if (response) {
          updateStatus({
            status: response.status,
            subStatus: response.subStatus
          });
          history.push(routes.home.new);
        }
      });
    }
  };

  const downloadPDF = (documentType: string) => {
    generatePDF(documentType, response => {
      if (response) {
        downloadBlobFile(response, documentType);
      }
    });
  };

  const toolTip = (information: string) => (
    <Tooltip title={information} placement="top">
      <Icon
        component={() => <img src={InfoIcon} alt="Information" />}
        className="ml-1"
      />
    </Tooltip>
  );

  const renderForm = (
    <div css={KYCStyles}>
      <Formik
        initialValues={documents.data}
        dirty={false}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
      >
        {({ values, setFieldValue }) => (
          <Form>
            {(fieldCounter = 1) && ""}
            <Text className="pl-4 upload-header">
              Select & Upload the proof
            </Text>
            <Row gutter={16} type="flex">
              <Col span={12} className="pr-5 pl-4">
                <Text className="d-flex align-items-center my-3 file-upload-title">
                  {fieldCounter++ + ". " + CONSTANTS.titles.POI}
                  <span className="mandatory-custom-style pl-1">*</span>
                </Text>
                <div className="d-flex flex-column field-seperator pb-2">
                  <FormDropdown
                    name="POIDocType"
                    options={documents.options.POI}
                    loading={false}
                    label={CONSTANTS.labels.POIDropdown}
                    placeholder={CONSTANTS.placeholders.POI}
                    className="file-type-container"
                    disable={disableKYC}
                  />
                  <FormFileUpload
                    name="POI"
                    hideStaleSubDocTypeFiles={!dmiInitiated}
                    className="upload-button"
                    fileList={values.POI}
                    disableUpload={!values.POIDocType}
                    disableForm={disableKYC}
                    onAdd={() => {
                      setFieldValue("POIDocType", "");
                    }}
                    config={fileConfig("POI", values.POIDocType)}
                    label={CONSTANTS.labels.POI}
                    acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                  />
                </div>
              </Col>
              <Col span={12} className="pr-5 pl-4">
                <Text className="d-flex align-items-center my-3 file-upload-title">
                  {fieldCounter++ + ". " + CONSTANTS.titles.POA}
                  <span className="mandatory-custom-style pl-1">*</span>
                </Text>
                <div className="d-flex flex-column field-seperator pb-2">
                  <FormDropdown
                    name="POADocType"
                    options={documents.options.POA}
                    loading={false}
                    label={CONSTANTS.labels.POADropdown}
                    placeholder={CONSTANTS.placeholders.POA}
                    className="file-type-container"
                    disable={disableKYC}
                  />
                  <FormFileUpload
                    name="POA"
                    hideStaleSubDocTypeFiles={!dmiInitiated}
                    className="upload-button"
                    fileList={values.POA}
                    disableUpload={!values.POADocType}
                    disableForm={disableKYC}
                    onAdd={() => {
                      setFieldValue("POADocType", "");
                    }}
                    config={fileConfig("POA", values.POADocType)}
                    label={CONSTANTS.labels.POA}
                    acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                  />
                </div>
              </Col>
              <Col span={12} className="pr-5 pl-4">
                <Text className="d-flex align-items-center my-3 file-upload-title">
                  {fieldCounter++ + ". " + CONSTANTS.titles.POB}
                  <span className="mandatory-custom-style pl-1">*</span>
                  {toolTip(CONSTANTS.feedbacks.POBTooltip)}
                </Text>
                <div className="d-flex flex-column field-seperator pb-2">
                  <FormDropdown
                    name="POBDocType"
                    options={documents.options.POB}
                    loading={false}
                    label={CONSTANTS.labels.POBDropdown}
                    placeholder={CONSTANTS.placeholders.POB}
                    className="file-type-container"
                    disable={disableKYC}
                  />
                  <FormFileUpload
                    name="POB"
                    hideStaleSubDocTypeFiles={!dmiInitiated}
                    className="upload-button"
                    fileList={values.POB}
                    disableUpload={!values.POBDocType}
                    disableForm={disableKYC}
                    onAdd={() => {
                      setFieldValue("POBDocType", "");
                    }}
                    config={fileConfig("POB", values.POBDocType)}
                    label={CONSTANTS.labels.POB}
                    acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                  />
                </div>
              </Col>
              {values["Company PAN"] && (
                <Col span={12} className="pr-5 pl-4">
                  <Text className="d-flex align-items-center mt-3 file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.companyPAN}
                    <span className="mandatory-custom-style pl-1">*</span>
                  </Text>
                  <div className="d-flex flex-column field-seperator pb-2">
                    <FormFileUpload
                      name="Company PAN"
                      hideStaleSubDocTypeFiles={!dmiInitiated}
                      className="upload-button"
                      fileList={values["Company PAN"]}
                      disableForm={disableKYC}
                      config={fileConfig("Company PAN")}
                      label={CONSTANTS.labels.companyPAN}
                      acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                    />
                  </div>
                </Col>
              )}
              {values["Partnership Deed"] && (
                <Col span={12} className="pr-5 pl-4">
                  <Text className="d-flex align-items-center mt-3 file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.partnershipDeed}
                    <span className="mandatory-custom-style pl-1">*</span>
                  </Text>
                  <div className="d-flex flex-column field-seperator pb-2">
                    <FormFileUpload
                      name="Partnership Deed"
                      hideStaleSubDocTypeFiles={!dmiInitiated}
                      className="upload-button"
                      fileList={values["Partnership Deed"]}
                      disableForm={disableKYC}
                      config={fileConfig("Partnership Deed")}
                      label={CONSTANTS.labels.partnershipDeed}
                      acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                    />
                  </div>
                </Col>
              )}
              {values["Partnership Authority Letter"] && (
                <Col span={12} className="pr-5 pl-4">
                  <Text className="d-flex align-items-center mt-3 file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.PAL}
                    <span className="mandatory-custom-style pl-1">*</span>
                  </Text>
                  <div className="d-flex flex-column field-seperator pb-2">
                    <FormFileUpload
                      name="Partnership Authority Letter"
                      hideStaleSubDocTypeFiles={!dmiInitiated}
                      className="upload-button"
                      fileList={values["Partnership Authority Letter"]}
                      disableForm={disableKYC}
                      config={fileConfig("Partnership Authority Letter")}
                      label={CONSTANTS.labels.PAL}
                      acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                    />
                  </div>
                </Col>
              )}
              {values["MOA/AOA"] && (
                <Col span={12} className="pr-5 pl-4">
                  <Text className="d-flex align-items-center mt-3 file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.MOA}
                    <span className="mandatory-custom-style pl-1">*</span>
                  </Text>
                  <div className="d-flex flex-column field-seperator pb-2">
                    <FormFileUpload
                      name="MOA/AOA"
                      hideStaleSubDocTypeFiles={!dmiInitiated}
                      className="upload-button"
                      fileList={values["MOA/AOA"]}
                      disableForm={disableKYC}
                      config={fileConfig("MOA/AOA")}
                      label={CONSTANTS.labels.MOA}
                      acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                    />
                  </div>
                </Col>
              )}
              {values["Board Resolution"] && (
                <Col span={12} className="pr-5 pl-4">
                  <Text className="d-flex align-items-center mt-3 file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.resolution}
                    <span className="mandatory-custom-style pl-1">*</span>
                  </Text>
                  <div className="d-flex flex-column field-seperator pb-2">
                    <FormFileUpload
                      hideStaleSubDocTypeFiles={!dmiInitiated}
                      name="Board Resolution"
                      className="upload-button"
                      fileList={values["Board Resolution"]}
                      disableForm={disableKYC}
                      config={fileConfig("Board Resolution")}
                      label={CONSTANTS.labels.resolution}
                      acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                    />
                  </div>
                </Col>
              )}
              {values["List of Director"] && (
                <Col span={12} className="pr-5 pl-4">
                  <Text className="d-flex align-items-center mt-3 file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.directors}
                    <span className="mandatory-custom-style pl-1">*</span>
                  </Text>
                  <div className="d-flex flex-column field-seperator pb-2">
                    <FormFileUpload
                      name="List of Director"
                      hideStaleSubDocTypeFiles={!dmiInitiated}
                      className="upload-button"
                      fileList={values["List of Director"]}
                      disableForm={disableKYC}
                      config={fileConfig("List of Director")}
                      label={CONSTANTS.labels.directors}
                      acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                    />
                  </div>
                </Col>
              )}
              {values["List of Shareholder"] && (
                <Col span={12} className="pr-5 pl-4">
                  <Text className="d-flex align-items-center mt-3 file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.shareholders}
                    <span className="mandatory-custom-style pl-1">*</span>
                  </Text>
                  <div className="d-flex flex-column field-seperator pb-2">
                    <FormFileUpload
                      name="List of Shareholder"
                      hideStaleSubDocTypeFiles={!dmiInitiated}
                      className="upload-button"
                      fileList={values["List of Shareholder"]}
                      disableForm={disableKYC}
                      config={fileConfig("List of Shareholder")}
                      label={CONSTANTS.labels.shareholders}
                      acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                    />
                  </div>
                </Col>
              )}
              <Col span={12} className="pr-5 pl-4">
                <Text className="d-flex align-items-center mt-3 file-upload-title">
                  {fieldCounter++ + ". " + CONSTANTS.titles.postDatedCheck}
                  <span className="mandatory-custom-style pl-1">*</span>
                </Text>
                <div className="d-flex flex-column field-seperator pb-2">
                  <FormFileUpload
                    name="Post-dated Cheque"
                    hideStaleSubDocTypeFiles={!dmiInitiated}
                    className="upload-button"
                    fileList={values["Post-dated Cheque"]}
                    disableForm={disableKYC}
                    config={fileConfig("Post-dated Cheque")}
                    label={CONSTANTS.labels.postDatedCheck}
                    acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                  />
                </div>
              </Col>
              <Col span={12} className="pr-5 pl-4 mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Text className="d-flex align-items-center file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.nachFileScan}
                    {toolTip(CONSTANTS.feedbacks.downloadTooltip)}
                  </Text>
                  <Button
                    type="link"
                    disabled={disableKYC}
                    onClick={() => downloadPDF("nach")}
                    className="download-pdf p-0"
                  >
                    <Icon type="download" />
                  </Button>
                </div>
                <div className="d-flex flex-column field-seperator pb-2">
                  <FormFileUpload
                    name="Nach File Scan"
                    hideStaleSubDocTypeFiles={!dmiInitiated}
                    className="upload-button"
                    fileList={values["Nach File Scan"]}
                    disableForm={disableKYC}
                    config={fileConfig("Nach File Scan")}
                    label={CONSTANTS.labels.nachFileScan}
                    acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                  />
                </div>
              </Col>
            </Row>
            <Divider dashed />
            <Text className="pl-4 upload-header">
              Download the form & upload the signed, scanned copies
            </Text>
            <Row gutter={16} type="flex">
              <Col span={12} className="pr-5 pl-4 mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Text className="d-flex align-items-center file-upload-title">
                    {fieldCounter++ +
                      ". " +
                      CONSTANTS.titles.loanApplicationForm}
                    <span className="mandatory-custom-style pl-1">*</span>
                    {toolTip(CONSTANTS.feedbacks.downloadTooltip)}
                  </Text>
                  <Button
                    type="link"
                    disabled={disableKYC}
                    onClick={() => downloadPDF("loan_application_form")}
                    className="download-pdf p-0"
                  >
                    <Icon type="download" />
                  </Button>
                </div>
                <div className="d-flex flex-column field-seperator pb-2">
                  <FormFileUpload
                    name="Loan Application Form"
                    hideStaleSubDocTypeFiles={!dmiInitiated}
                    className="upload-button"
                    fileList={values["Loan Application Form"]}
                    disableForm={disableKYC}
                    config={fileConfig("Loan Application Form")}
                    label={CONSTANTS.labels.loanApplicationForm}
                    acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                  />
                </div>
              </Col>
              <Col span={12} className="pr-5 pl-4 mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Text className="d-flex align-items-center file-upload-title">
                    {fieldCounter++ + ". " + CONSTANTS.titles.loanDetailSheet}
                    <span className="mandatory-custom-style pl-1">*</span>
                    {toolTip(CONSTANTS.feedbacks.downloadTooltip)}
                  </Text>
                  <Button
                    type="link"
                    disabled={disableKYC}
                    onClick={() => downloadPDF("loan_details_sheets")}
                    className="download-pdf p-0"
                  >
                    <Icon type="download" />
                  </Button>
                </div>
                <div className="d-flex flex-column field-seperator pb-2">
                  <FormFileUpload
                    name="Loan Detail Sheet"
                    hideStaleSubDocTypeFiles={!dmiInitiated}
                    className="upload-button"
                    fileList={values["Loan Detail Sheet"]}
                    disableForm={disableKYC}
                    config={fileConfig("Loan Detail Sheet")}
                    label={CONSTANTS.labels.loanDetailSheet}
                    acceptableFileExtensions={KYC_SUPPORTED_EXTENSIONS}
                  />
                </div>
              </Col>
            </Row>
            {!disableKYC && (
              <Row gutter={16} type="flex">
                <Col span={2} offset={22}>
                  <SubmitButton
                    htmlType="submit"
                    containerDivClassName="justify-content-md-end mb-5"
                    type="primary"
                    className="mt-4"
                    loading={KYCSubmit.loading}
                    disabled={KYCSubmit.loading || disableKYC}
                  >
                    {CONSTANTS.buttons.submit}
                  </SubmitButton>
                </Col>
              </Row>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
  return (
    <React.Fragment>
      {/* render form only after fetching uploaded KYC details */}
      {!getDocumentsList.loading && !getUploadedDocuments.loading && renderForm}
    </React.Fragment>
  );
};

export default KYC;
